import React, { FunctionComponent, ReactNode, useCallback } from 'react'

import {
  Box,
  BoxProps,
  Flex,
  FlexAnchor,
  Icon,
  smBumps,
  Typo,
  xsBumps,
  xxsBumps,
} from '../atoms'
import { HelpLinkIFrame } from './HelpLinkIFrame'
import { useModal } from './Modal'

export interface PremiumLineItem {
  header: ReactNode
  currentAmount: string
  previousAmount?: string
  contextualHelpCode?: string
}

export interface PremiumBreakdownProps extends BoxProps {
  lineItems: PremiumLineItem[]
  additionalLineItems?: string[]
  ipt: ReactNode
  extraInfo?: ReactNode
  contentBaseUrl: string
}

export const PremiumBreakdown: FunctionComponent<PremiumBreakdownProps> = ({
  lineItems,
  additionalLineItems,
  ipt,
  extraInfo,
  contentBaseUrl,
  ...props
}) => {
  const modal = useModal()

  const onHelpClick = useCallback(
    (code: string) => {
      modal.show({
        content: <HelpLinkIFrame src={`${contentBaseUrl}/${code}.html`} />,
        variant: 'iframe',
      })
    },
    [contentBaseUrl, modal],
  )

  return (
    <Box {...props}>
      {lineItems.map(
        ({ header, previousAmount, currentAmount, contextualHelpCode }, i) => (
          <Flex width={1} key={`premium-line-item-${i}`} my={smBumps} gap={smBumps}>
            <Flex justifyContent='flex-start' gap={2}>
              <Typo typoSize='sm'>{header}</Typo>
              {contextualHelpCode && (
                <FlexAnchor onClick={() => onHelpClick(contextualHelpCode)}>
                  <Icon icon='info' size='tiny' />
                </FlexAnchor>
              )}
            </Flex>
            <Flex>
              {previousAmount && previousAmount !== currentAmount && (
                <Typo mr={xsBumps} color='inactive' typoSize='sm'>
                  <s>{previousAmount}</s>
                </Typo>
              )}
              <Typo typoSize='sm'>{currentAmount}</Typo>
            </Flex>
          </Flex>
        ),
      )}
      {extraInfo}
      {(additionalLineItems || ipt) && (
        <Flex
          flexDirection='column'
          width={1}
          borderTopWidth={1}
          py={smBumps}
          gap={xxsBumps}
        >
          {additionalLineItems?.map((it, i) => (
            <Box width={1} key={`additional-premium-line-item-${i}`}>
              <Typo typoSize='xs' color='bodySecondary'>
                {it}
              </Typo>
            </Box>
          ))}
          {ipt && (
            <Box width={1}>
              <Typo typoSize='xs' color='bodySecondary'>
                {ipt}
              </Typo>
            </Box>
          )}
        </Flex>
      )}
    </Box>
  )
}
